// Components/Tabs/ToolCategoriesTab.jsx

import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";



// Importing components
import Slider from "../Slider/Slider";
import DataTable from "../DataTable/DataTable";
import ResourceTabs from "../ResourceTabs/ResourceTabs";
import Backend from "../../../utils/api.js";
import ArchiveResourceDialog from "../ArchiveResourceDialog";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";
/**
 * ToolCategoriesTab Component
 * 
 * This component allows administrators to add or remove tool categories within a lab.
 * It displays a data table of existing tool categories and provides functionalities
 * to update or add new categories.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function ToolCategoriesTab({ user, labId, pushTab, revert }) {
  useDocumentTitle("Admin: Add or Remove Tool Categories in Space Tab – Pinpoint");

  const [refreshToggle, setRefreshToggle] = useState(false);
  
  const [categoryToArchive, setCategoryToArchive] = useState(null);
  const activeTabRef = useRef(null);
  const returnFocusRef = useRef(null);
  const fetchAllToolCategories = useCallback(() => Backend.getToolCategoriesByLab(user.UID, labId, 1000), [user.UID, labId]);
  const loadAffected = useCallback(async () => {
    const response = await Backend.getMachineByLab(user.UID, labId);
    return { ...response, items: response.items?.filter(instance =>
      String(instance.categoryID) === String(categoryToArchive?.categoryID)) };
  }, [user.UID, labId, categoryToArchive]);

  // Function to fetch active tool categories
  const activeDataFunction = useCallback(
    () =>
      fetchAllToolCategories().then((res) => {
        if (res.error || !res.items) return { items: [], status: 0 };
        return { ...res, items: res.items.filter((tc) => !Number(tc.isDeleted)) };
      }),
    [fetchAllToolCategories, refreshToggle]
  );

  // Function to fetch archived tool categories
  const archivedDataFunction = useCallback(
    () =>
      Backend.getToolCategoriesByLab(user.UID, labId, 1000, true).then((res) => {
        if (res.error || !res.items) return { items: [], status: 0 };
        return { ...res, items: res.items.filter((tc) => Number(tc.isDeleted)) };
      }),
    [user.UID, labId, refreshToggle]
  );

  return (
    <Slider
      name="Add/remove tool categories"
      navColor="rgb(146, 185, 247)"
      slide={true}
      index={0} // Starting index for the tool categories tab
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
    >
      {/* DataTable displays the list of tool categories */}
      <ResourceTabs activeTabRef={activeTabRef}>
      <DataTable
        title="Your space's tool categories"
        columns={[
          { title: "Category name", field: "categoryName" },
        ]}
        dataFunction={activeDataFunction}
        submitRowChange={(updatedToolCategory) =>
          Backend.updateToolCategory(
            user.UID,
            updatedToolCategory.categoryID,
            updatedToolCategory.categoryName,
            labId
          ) // Handle updating existing tool category
        }
        submitNewRow={(newToolCategory) =>
          Backend.newToolCategory(
            user.UID,
            newToolCategory.categoryName,
            labId
          ) // Handle adding a new tool category
        }
        key="active"
        customActions={[
          (rowData) => ({
            icon: 'archive',
            tooltip: 'Archive tool category',
            onClick: (event) => {
              returnFocusRef.current = event.currentTarget;
              setCategoryToArchive(rowData);
            },
            hidden: false,
          }),
        ]}
      />

      {/* ARCHIVED tool categories */}
      <DataTable
        title="Archived tool categories"
        columns={[
          { title: "Category name", field: "categoryName" },
        ]}
        dataFunction={archivedDataFunction}
        submitRowChange={(updatedToolCategory) =>
          Backend.updateToolCategory(
            user.UID,
            updatedToolCategory.categoryID,
            updatedToolCategory.categoryName,
            labId
          )
        }
        key="archived"
        customActions={[
          (rowData) => ({
            icon: 'unarchive',
            tooltip: 'Unarchive tool category',
            onClick: () => Backend.toggleToolCategoryArchive(user.UID, rowData.categoryID, rowData.categoryName, labId, false),
          }),
        ]}
      />
      </ResourceTabs>

      {categoryToArchive && <ArchiveResourceDialog
        label="tool category"
        name={categoryToArchive.categoryName}
        loadAffected={loadAffected}
        onArchive={() => Backend.toggleToolCategoryArchive(user.UID, categoryToArchive.categoryID, categoryToArchive.categoryName, labId, true)}
        onClose={() => setCategoryToArchive(null)}
        onArchived={() => {
          returnFocusRef.current = activeTabRef.current;
          setCategoryToArchive(null);
          setRefreshToggle(value => !value);
        }}
        finalFocusRef={returnFocusRef}
        affectedDescription="Tool instances that will no longer be visible to users"
        emptyDescription="No tool instances are currently assigned to this category."
      />}
    </Slider>
  );
}

// PropTypes for type-checking component props
ToolCategoriesTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default ToolCategoriesTab;
